import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import {
  ArrowRight, ArrowLeft, CheckCircle, Clock, Mail, Phone, MapPin,
  User, BookOpen, Shield, GraduationCap, Sparkles, CreditCard,
  Users, Calendar, ChevronRight, Monitor, Home
} from 'lucide-react'
import { courseImages } from '../../components/landing/landingData'
import { fetchAdvisorApplicationContext } from '../../services/advisorLeadService'

const steps = [
  { id: 1, label: 'Personal' },
  { id: 2, label: 'Program' },
  { id: 3, label: 'Details' },
  { id: 4, label: 'Account' },
]

const trustIndicators = [
  { icon: Users, label: '50+', desc: 'Graduates' },
  { icon: GraduationCap, label: 'Industry', desc: 'Training' },
  { icon: Sparkles, label: 'AI', desc: 'Integrated' },
  { icon: CreditCard, label: 'Flexible', desc: 'Payment' },
  { icon: Shield, label: 'Certified', desc: 'Programs' },
]

const journeySteps = [
  { num: '01', title: 'Submit Application', desc: 'Complete your profile in under 5 minutes' },
  { num: '02', title: 'Pay Admission Fee', desc: '50,000 UGX registration fee — campus or Mobile Money' },
  { num: '03', title: 'Admissions Review', desc: 'We confirm payment and review your application' },
  { num: '04', title: 'Account Activated', desc: 'You receive an email when you can log in' },
  { num: '05', title: 'Start Training', desc: 'Join your cohort and begin your creative journey' },
]

const postSubmitSteps = [
  {
    title: 'Pay the 50,000 UGX admission fee within 7 days',
    desc: 'Your application is reserved for 7 days. Pay the registration fee before the deadline or your application will expire.',
  },
  {
    title: 'Wait for admissions approval',
    desc: 'Our team reviews your application after payment is confirmed. This usually takes 24–48 hours.',
  },
  {
    title: 'Check your email',
    desc: 'We sent a confirmation to your inbox. You will receive another email when your account is active.',
  },
]

export default function Register() {
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState(1)
  const [courses, setCourses] = useState([])
  const [loadingCourses, setLoadingCourses] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [openIntakes, setOpenIntakes] = useState([])
  // Phase 2C: Holiday program support
  const [holidayMode, setHolidayMode] = useState(false)
  const [holidayOfferings, setHolidayOfferings] = useState([])
  const [holidayIntakes, setHolidayIntakes] = useState([])
  // Courses offered by the selected intake, resolved server-side from the
  // admin Intake → Offerings configuration. The applicant never sees the
  // global course catalogue during registration.
  const [offerings, setOfferings] = useState([])
  const [loadingOfferings, setLoadingOfferings] = useState(false)
  const [offeringsNotice, setOfferingsNotice] = useState(null)
  const [preferredCourseCode, setPreferredCourseCode] = useState(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    user_type: 'student',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    course_ids: [],
    study_mode: '',
    preferred_study_time: '',
    study_days: '',
    intake_id: '',
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [advisorPrefill, setAdvisorPrefill] = useState(false)

  useEffect(() => {
    document.title = 'Apply Now | Synergy Multimedia Academy Admissions'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) metaDesc.setAttribute('content', 'Apply to Synergy Multimedia Academy — Uganda\'s premier AI-native creative institution. Limited seats for the 2026 intake. Photography, Film, Design & AI.')
  }, [])

  // Fetch available courses on mount, and prefill from a server-resolved advisor cid.
  // Phase 2C: Also detect ?program_type=HOLIDAY and fetch holiday offerings.
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const programTypeParam = searchParams.get('program_type')
        const isHoliday = programTypeParam && programTypeParam.toUpperCase() === 'HOLIDAY'
        setHolidayMode(isHoliday)

        // Fetch courses — filter by program_type if holiday mode
        const coursesUrl = isHoliday ? '/auth/courses?program_type=HOLIDAY' : '/auth/courses'
        const response = await api.get(coursesUrl)
        setCourses(response.data)

        // Phase 2C: If holiday mode, fetch holiday offerings to get available intakes
        let holidayData = null
        if (isHoliday) {
          try {
            const { data: offerings } = await api.get('/auth/holiday-offerings')
            setHolidayOfferings(offerings?.courses || [])
            // Extract unique intakes from offerings that have open registrations
            const intakeMap = new Map()
            ;(offerings?.courses || []).forEach((c) => {
              if (c.is_registerable) {
                c.offerings.forEach((o) => {
                  if (!intakeMap.has(o.intake_id)) {
                    intakeMap.set(o.intake_id, {
                      id: o.intake_id,
                      name: o.intake_name,
                      code: o.intake_code,
                      status: o.intake_status,
                      start_date: o.start_date,
                      end_date: o.end_date,
                      registration_close_date: o.registration_close_date,
                    })
                  }
                })
              }
            })
            setHolidayIntakes(Array.from(intakeMap.values()))
          } catch (err) {
            console.warn('Holiday offerings unavailable:', err)
          }
        }

        const cid = searchParams.get('cid')
        let advisorCourseCode = null
        if (cid) {
          try {
            const context = await fetchAdvisorApplicationContext(cid)
            const lead = context?.lead
            if (lead) {
              advisorCourseCode = lead.recommendedCourseCode || null
              const hasAny = Boolean(lead.firstName || lead.lastName || lead.phone || lead.email || advisorCourseCode)
              setFormData((prev) => {
                const next = { ...prev }
                if (!String(prev.first_name || '').trim() && lead.firstName) next.first_name = lead.firstName
                if (!String(prev.last_name || '').trim() && lead.lastName) next.last_name = lead.lastName
                if (!String(prev.phone || '').trim() && lead.phone) next.phone = lead.phone
                if (!String(prev.email || '').trim() && lead.email) next.email = lead.email
                return next
              })
              if (hasAny) setAdvisorPrefill(true)
            }
          } catch {
            // Missing or invalid cid keeps the normal blank application.
          }
        }

        // A preferred course (advisor recommendation or ?course=) is applied
        // once the selected intake's offerings are known — a course is only
        // selectable if that intake actually offers it.
        setPreferredCourseCode(advisorCourseCode || searchParams.get('course') || null)

        // Phase 2C: Pre-select intake from URL param (?intake_id=...)
        const intakeIdParam = searchParams.get('intake_id')
        if (intakeIdParam) {
          setFormData((prev) => (
            prev.intake_id ? prev : { ...prev, intake_id: String(intakeIdParam) }
          ))
        }
      } catch (error) {
        console.error('Failed to fetch courses:', error)
      } finally {
        setLoadingCourses(false)
      }
    }
    fetchCourses()
  }, [searchParams])

  // Phase 4.2: Fetch all OPEN registration intakes (dynamic, never hardcoded)
  // Phase 2C: In holiday mode, skip this — holiday intakes come from holiday-offerings.
  useEffect(() => {
    if (holidayMode) return
    const fetchOpenIntakes = async () => {
      try {
        const { data } = await api.get('/intakes/open')
        if (data?.intakes?.length) {
          setOpenIntakes(data.intakes)
          // The applicant's own choice decides the intake. Only when a single
          // intake is open (no choice to make) is one selected automatically;
          // with several open the field stays empty until the applicant picks,
          // unless a campaign link already pre-selected one.
          if (data.intakes.length === 1) {
            setFormData((prev) => (prev.intake_id ? prev : { ...prev, intake_id: String(data.intakes[0].id) }))
          }
        }
      } catch (error) {
        console.warn('Open intakes unavailable:', error)
      }
    }
    fetchOpenIntakes()
  }, [holidayMode])

  // Phase 2C: When holiday intakes are loaded, pre-select from the URL param.
  // Same rule as regular mode: auto-select only when there is nothing to
  // choose between; a campaign link's ?intake_id= always wins.
  useEffect(() => {
    if (!holidayMode || holidayIntakes.length === 0) return
    setFormData((prev) => {
      if (prev.intake_id) return prev // already set from URL param
      if (holidayIntakes.length !== 1) return prev
      return { ...prev, intake_id: String(holidayIntakes[0].id) }
    })
  }, [holidayMode, holidayIntakes])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      const { confirmPassword, ...dataToSend } = formData
      if (formData.course_ids.length > 0) {
        dataToSend.course_ids = formData.course_ids.map(Number)
      } else {
        delete dataToSend.course_ids
      }
      // Phase 4.1: Pass study_mode to server (required: PHYSICAL or ONLINE)
      if (formData.study_mode) {
        dataToSend.study_mode = formData.study_mode
      }
      // Phase 4.0: Pass study_days to server (server validates against allowed values)
      if (formData.study_days) {
        dataToSend.study_days = formData.study_days
      }
      // Phase 4.2: Pass selected intake_id to server
      if (formData.intake_id) {
        dataToSend.intake_id = Number(formData.intake_id)
      }
      const advisorConversationId = searchParams.get('cid')
      if (advisorConversationId) {
        const selectedCourse = courses.find((course) => String(course.id) === String(formData.course_ids[0]))
        dataToSend.advisor_conversation_id = advisorConversationId
        dataToSend.advisor_course_code = selectedCourse?.course_code || searchParams.get('course') || null
      }
      const response = await api.post('/auth/register', dataToSend)
      
      // Account is pending approval — show success screen
      toast.success('Registration submitted!')
      setSubmitted(true)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const validateStep = (s) => {
    const newErrors = {}
    if (s === 1) {
      if (!formData.first_name.trim()) newErrors.first_name = 'First name is required'
      if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required'
      if (!formData.email.trim()) newErrors.email = 'Email is required'
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email'
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required'
    }
    if (s === 2) {
      if (formData.course_ids.length === 0) newErrors.courses = 'Select at least one program'
      if (!formData.intake_id) newErrors.intake_id = 'Select an intake'
    }
    if (s === 3) {
      if (!formData.study_mode) newErrors.study_mode = 'Select a study mode (Physical or Online)'
    }
    if (s === 4) {
      if (!formData.password) newErrors.password = 'Password is required'
      else if (formData.password.length < 6) newErrors.password = 'Minimum 6 characters'
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(step)) setStep((p) => Math.min(p + 1, 4))
  }

  // ─── Intake → Offerings → Courses ────────────────────────────────────────
  // Whenever the selected intake changes, load that intake's offerings from
  // the server and reconcile the current course selection against them:
  // courses still offered stay selected, courses that are not are dropped with
  // an explicit notice. Nothing is filtered client-side from a global list.
  useEffect(() => {
    const intakeId = formData.intake_id
    if (!intakeId) {
      setOfferings([])
      setOfferingsNotice(null)
      return undefined
    }
    let cancelled = false
    const loadOfferings = async () => {
      setLoadingOfferings(true)
      try {
        const { data } = await api.get(`/intakes/${intakeId}/offerings`)
        if (cancelled) return
        const list = (data?.courses || []).map((c) => ({
          ...c,
          // The registration UI reads course_name; the public payload sends title.
          course_name: c.title,
        }))
        setOfferings(list)

        const offeredIds = new Set(list.map((c) => String(c.id)))
        setFormData((prev) => {
          let next = prev
          const kept = prev.course_ids.filter((id) => offeredIds.has(String(id)))
          if (kept.length !== prev.course_ids.length) {
            const intakeName = data?.intake?.name || 'this intake'
            setOfferingsNotice(
              `You've changed your intake. Please select a programme available for ${intakeName}.`
            )
            next = { ...next, course_ids: kept }
          } else if (kept.length > 0) {
            // A still-valid selection is never cleared.
            setOfferingsNotice(null)
          }

          // Apply a preferred course (advisor recommendation or ?course=) only
          // if this intake offers it.
          if (next.course_ids.length === 0 && preferredCourseCode) {
            const matched = list.find((c) => c.course_code === preferredCourseCode)
            if (matched) next = { ...next, course_ids: [String(matched.id)] }
          }
          return next
        })
      } catch (err) {
        if (cancelled) return
        setOfferings([])
        if (err?.response?.status === 404) {
          setOfferingsNotice('This intake is not open for applications. Choose another intake.')
        } else {
          setOfferingsNotice('Could not load the programmes for this intake. Please try again.')
        }
      } finally {
        if (!cancelled) setLoadingOfferings(false)
      }
    }
    loadOfferings()
    return () => { cancelled = true }
  }, [formData.intake_id, preferredCourseCode])

  // The intake this application is for. It is whatever the applicant selected
  // (or a campaign link pre-selected) — never the primary intake and never the
  // first open one. With several intakes open and none chosen yet, the form
  // says so instead of showing another intake's dates.
  const intakeOptions = holidayMode ? holidayIntakes : openIntakes
  const chosenIntake =
    intakeOptions.find((i) => String(i.id) === String(formData.intake_id)) || null
  const bannerIntake = chosenIntake || (intakeOptions.length === 1 ? intakeOptions[0] : null)

  const prevStep = () => setStep((p) => Math.max(p - 1, 1))

  // Students pick from the selected intake's offerings; trainers pick from the
  // full catalogue of courses they could teach.
  const selectableCourses = formData.user_type === 'trainer' ? courses : offerings
  const selectedCourses = selectableCourses.filter((c) => formData.course_ids.includes(String(c.id)))

  return (
    <div className="min-h-screen bg-cinema-black">
      {/* Mobile Trust Banner — shows first on small screens */}
      <div className="lg:hidden relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Gallery/Academy/124414.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-cinema-black" />
        <div className="relative px-6 py-16 text-center">
          <img src="/Logos/SMA LOGO  DARK MODE.png" alt="SMA" className="h-12 mx-auto mb-6 opacity-90" />
          <h1 className="font-display text-3xl sm:text-4xl text-white font-light leading-tight">
            Begin your<br /><span className="text-cinema-gold">creative career</span>
          </h1>
          <p className="mt-4 text-cinema-soft/70 max-w-sm mx-auto">
            Join Uganda's premier AI-native creative institution.
          </p>
          <div className="mt-8 flex justify-center gap-6 flex-wrap">
            {trustIndicators.slice(0, 3).map((t) => (
              <div key={t.desc} className="text-center">
                <t.icon className="w-5 h-5 text-cinema-gold mx-auto mb-1" />
                <p className="text-white font-display text-sm">{t.label}</p>
                <p className="text-cinema-muted text-xs uppercase tracking-wider font-medium">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="min-h-screen flex">
        {/* Left Column — Trust & Story (55% desktop) */}
        <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Gallery/Academy/124414.jpg')" }} />
          <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/80" />

          <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
            {/* Top */}
            <div>
              <img src="/Logos/SMA LOGO  DARK MODE.png" alt="SMA" className="h-10 opacity-90" />
            </div>

            {/* Middle — Journey */}
            <div className="max-w-md">
              <span className="cinema-label">Admissions Journey</span>
              <h2 className="font-display text-4xl text-white font-light mt-6 mb-10 leading-tight">
                Your future starts<br />with <span className="text-cinema-gold">one application</span>
              </h2>
              <div className="space-y-6">
                {journeySteps.map((js) => (
                  <div key={js.num} className="flex gap-4 items-start">
                    <span className="text-cinema-gold/60 font-display text-sm mt-0.5">{js.num}</span>
                    <div>
                      <p className="text-white font-display text-base">{js.title}</p>
                      <p className="text-cinema-muted text-sm font-sans mt-0.5">{js.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom — Trust + Contact */}
            <div className="space-y-8">
              <div className="flex gap-8">
                {trustIndicators.map((t) => (
                  <div key={t.desc} className="text-center">
                    <t.icon className="w-5 h-5 text-cinema-gold mx-auto mb-2" />
                    <p className="text-white font-display text-lg">{t.label}</p>
                    <p className="text-cinema-muted text-xs uppercase tracking-wider mt-1 font-medium">{t.desc}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 pt-6">
                <p className="text-cinema-muted text-sm font-sans">Need help with your application?</p>
                <div className="mt-3 flex gap-6 text-sm text-white/80">
                  <a href="tel:+256706739311" className="flex items-center gap-2 hover:text-cinema-gold transition-colors">
                    <Phone className="w-4 h-4 text-cinema-gold" /> +256 706 739 311
                  </a>
                  <a href="mailto:synergymultimediaacademy@gmail.com" className="flex items-center gap-2 hover:text-cinema-gold transition-colors">
                    <Mail className="w-4 h-4 text-cinema-gold" /> Email Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column — Form (45% desktop) */}
        <div className="w-full lg:w-[45%] bg-cinema-black flex items-start justify-center px-6 sm:px-12 py-12 lg:py-16 overflow-y-auto">
          <div className="w-full max-w-lg">
            {/* Header (desktop only, mobile already has banner) */}
            <div className="hidden lg:block mb-10">
              <span className="cinema-label">Admissions</span>
              <h1 className="font-display text-3xl text-white font-light mt-4">
                Apply to <span className="text-cinema-gold">SMA</span>
              </h1>
              <p className="mt-3 text-cinema-muted text-sm font-sans">
                Complete your application in under 5 minutes.
              </p>
            </div>

            {/* Submitted State */}
            {submitted ? (
              <div className="space-y-8">
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-cinema-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-cinema-gold" />
                  </div>
                  <h2 className="font-display text-3xl text-white font-light">Application Received</h2>
                  <p className="mt-3 text-cinema-muted font-sans max-w-md mx-auto">
                    Thank you, {formData.first_name}. Your application is pending review.
                  </p>
                </div>

                <div className="rounded-sm border border-cinema-glow/25 bg-cinema-glow/10 p-5 text-left">
                  <p className="text-cinema-glow font-display text-sm font-medium mb-2">Registration Reservation — 7 Days</p>
                  <p className="text-cinema-soft/90 font-sans text-sm leading-relaxed">
                    Submitting this form does not mean you are fully registered. Your place is reserved for 7 days and is confirmed only after the registration fee is paid.
                  </p>
                </div>

                <div className="pt-2 border-t border-white/5">
                  <p className="cinema-label mb-6">What happens next</p>
                  <div className="space-y-5">
                    {postSubmitSteps.map((step, i) => (
                      <div key={step.title} className="flex gap-4">
                        <div className="w-6 h-6 rounded-full bg-cinema-gold/10 flex items-center justify-center shrink-0 mt-0.5">
                          <span className="text-cinema-gold text-xs font-display">{String(i + 1).padStart(2, '0')}</span>
                        </div>
                        <div>
                          <p className="text-white text-sm font-display">{step.title}</p>
                          <p className="text-cinema-muted text-xs font-sans mt-0.5">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-sm border border-white/10 bg-white/[0.02] p-5 space-y-3">
                  <p className="cinema-label">Need to pay or have questions?</p>
                  <div className="flex flex-col sm:flex-row gap-4 text-sm font-sans">
                    <a href="tel:+256706739311" className="flex items-center gap-2 text-white/90 hover:text-cinema-gold transition-colors">
                      <Phone className="w-4 h-4 text-cinema-gold shrink-0" /> +256 706 739 311
                    </a>
                    <a href="mailto:synergymultimediaacademy@gmail.com" className="flex items-center gap-2 text-white/90 hover:text-cinema-gold transition-colors">
                      <Mail className="w-4 h-4 text-cinema-gold shrink-0" /> synergymultimediaacademy@gmail.com
                    </a>
                  </div>
                </div>

                <Link to="/contact" className="block w-full cinema-btn-ghost text-center">
                  Contact Admissions
                </Link>

                <div className="pt-3 border-t border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Link
                    to="/"
                    className="flex items-center justify-center gap-2 rounded-sm border border-white/10 py-3 text-sm font-sans text-white/80 hover:text-white hover:border-white/25 transition-colors"
                  >
                    <Home className="w-4 h-4 text-cinema-gold" /> Back to Home
                  </Link>
                  <Link
                    to="/masterclass"
                    className="flex items-center justify-center gap-2 rounded-sm border border-white/10 py-3 text-sm font-sans text-white/80 hover:text-white hover:border-white/25 transition-colors"
                  >
                    <Sparkles className="w-4 h-4 text-cinema-gold" /> Learn About the Masterclass
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {/* Progress */}
                <div className="flex items-center gap-2 sm:gap-3 mb-10">
            {steps.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 sm:gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-display transition-colors ${
                  step === s.id ? 'bg-cinema-gold text-black' :
                  step > s.id ? 'bg-cinema-gold/20 text-cinema-gold' : 'bg-white/5 text-cinema-muted'
                }`}>
                  {step > s.id ? <CheckCircle className="w-4 h-4" /> : s.id}
                </div>
                <span className={`text-xs uppercase tracking-wider font-sans hidden sm:block ${
                  step === s.id ? 'text-cinema-gold' : step > s.id ? 'text-cinema-soft' : 'text-cinema-muted'
                }`}>{s.label}</span>
                {i < steps.length - 1 && (
                  <div className={`w-6 sm:w-8 h-px ${step > s.id ? 'bg-cinema-gold/40' : 'bg-white/5'}`} />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Dynamic intake banner — shows the intake being applied for */}
            {!bannerIntake && intakeOptions.length > 1 && (
              <div className="rounded-lg border border-white/15 bg-white/[0.03] px-5 py-4">
                <p className="font-display text-sm text-white font-semibold">
                  More than one intake is open
                </p>
                <p className="text-xs text-cinema-soft mt-1 font-sans">
                  Choose the intake you are applying for in step 02. Your application is filed
                  against the intake you select.
                </p>
              </div>
            )}
            {bannerIntake && (
              <div className="rounded-lg border border-cinema-gold/30 bg-cinema-gold/5 px-5 py-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-[0.65rem] uppercase tracking-[0.2em] text-cinema-muted font-sans mb-1">
                      Applying for
                    </p>
                    <p className="font-display text-lg text-cinema-gold font-semibold">
                      {bannerIntake.name}
                    </p>
                    <p className="text-xs text-cinema-soft mt-1 font-sans">
                      Registration {bannerIntake.status === 'OPEN' ? 'Open' : bannerIntake.status === 'CLOSING_SOON' ? 'Closing Soon' : bannerIntake.status}
                      {bannerIntake.registration_close_date && (
                        <> · Closes {new Date(bannerIntake.registration_close_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</>
                      )}
                    </p>
                  </div>
                  {bannerIntake.start_date && (
                    <p className="text-xs text-cinema-muted font-sans">
                      Training starts {new Date(bannerIntake.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-cinema-soft/80 font-sans">
                  <div>{holidayMode ? 'Duration: 6 weeks' : 'Study: 3 days per week'}</div>
                  <div>Times: 9:00 AM · 12:00 PM · 6:00 PM</div>
                  <div>Days: Mon/Wed/Fri or Tue/Thu/Sat</div>
                </div>
              </div>
            )}
            {/* Step 1: Personal */}
            {step === 1 && (
              <div className="space-y-8">
                <div>
                  <span className="cinema-label">Step 01</span>
                  <h2 className="font-display text-2xl text-white mt-3">Tell us about yourself</h2>
                  {advisorPrefill ? (
                    <p className="text-xs text-cinema-soft/80 font-sans mt-3">
                      Some details have been filled in from your conversation with SMA Admissions.
                    </p>
                  ) : null}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="cinema-label block mb-3">First Name</label>
                    <input type="text" className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0" placeholder="John" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required disabled={loading} />
                    {errors.first_name && <p className="text-red-400 text-xs mt-2">{errors.first_name}</p>}
                  </div>
                  <div>
                    <label className="cinema-label block mb-3">Last Name</label>
                    <input type="text" className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0" placeholder="Doe" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required disabled={loading} />
                    {errors.last_name && <p className="text-red-400 text-xs mt-2">{errors.last_name}</p>}
                  </div>
                </div>

                <div>
                  <label className="cinema-label block mb-3">Email</label>
                  <input type="email" className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0" placeholder="you@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required disabled={loading} />
                  {errors.email && <p className="text-red-400 text-xs mt-2">{errors.email}</p>}
                </div>

                <div>
                  <label className="cinema-label block mb-3">Phone</label>
                  <input type="tel" className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0" placeholder="0701234567" maxLength={20} value={formData.phone} onChange={(e) => { const val = e.target.value.replace(/[^0-9+\-\s()]/g, ''); setFormData({ ...formData, phone: val }) }} disabled={loading} />
                  {errors.phone && <p className="text-red-400 text-xs mt-2">{errors.phone}</p>}
                </div>

                <div>
                  <label className="cinema-label block mb-3">I am registering as</label>
                  <select className="w-full bg-transparent border-0 border-b border-white/10 text-white focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0" value={formData.user_type} onChange={(e) => setFormData({ ...formData, user_type: e.target.value })} required disabled={loading}>
                    <option value="student" className="bg-cinema-black">Student</option>
                    <option value="trainer" className="bg-cinema-black">Trainer</option>
                  </select>
                </div>
              </div>
            )}

            {/* Step 2: Program */}
            {step === 2 && (
              <div className="space-y-8">
                <div>
                  <span className="cinema-label">Step 02</span>
                  <h2 className="font-display text-2xl text-white mt-3">
                    {holidayMode ? 'Choose your holiday program & intake' : 'Choose your program & intake'}
                  </h2>
                  <p className="text-cinema-muted text-sm mt-2">
                    Your application will be reserved for 7 days. You must pay the registration fee within this period to secure your place.
                  </p>
                </div>

                {/* Phase 2C: Holiday mode banner */}
                {holidayMode && (
                  <div className="rounded-lg border border-cinema-gold/30 bg-cinema-gold/5 px-5 py-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-cinema-gold shrink-0 mt-0.5" />
                      <div>
                        <p className="font-display text-sm text-cinema-gold font-semibold">
                          Holiday Program Registration
                        </p>
                        <p className="text-xs text-cinema-soft mt-1 font-sans">
                          You are registering for a 6-week holiday program. Select a holiday intake below.
                          Only intakes that offer your chosen program will appear.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Intake selection — holiday mode uses holidayIntakes, regular uses openIntakes */}
                {(() => {
                  const intakesToShow = holidayMode ? holidayIntakes : openIntakes
                  if (intakesToShow.length === 0) {
                    return (
                      <div className="p-4 bg-red-500/10 border border-red-500/25 rounded-sm text-red-400 text-sm">
                        {holidayMode
                          ? 'No holiday intakes are currently open for registration. Please check back soon.'
                          : 'Registration is currently closed. Please check back when the next intake opens.'}
                      </div>
                    )
                  }
                  return (
                    <div>
                      <label className="cinema-label block mb-3">
                        {holidayMode ? 'Choose Your Holiday Intake' : 'Choose Your Intake'}
                      </label>
                      <div className="space-y-2">
                        {intakesToShow.map((intake) => (
                          <label key={intake.id} className={`flex items-center gap-3 p-3 cursor-pointer border rounded-sm ${formData.intake_id === String(intake.id) ? 'border-cinema-gold bg-cinema-gold/5' : 'border-white/10 hover:border-white/20'}`}>
                            <input type="radio" className="w-4 h-4 accent-cinema-gold" name="intake_id" value={String(intake.id)} checked={formData.intake_id === String(intake.id)} onChange={(e) => setFormData({ ...formData, intake_id: e.target.value })} disabled={loading} />
                            <div>
                              <p className="text-white text-sm font-medium">{intake.name}</p>
                              {intake.registration_close_date && (
                                <p className="text-cinema-muted text-xs">Registration closes {new Date(intake.registration_close_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                              )}
                              {holidayMode && intake.start_date && (
                                <p className="text-cinema-muted text-xs">
                                  {new Date(intake.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  {intake.end_date && ` – ${new Date(intake.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                                </p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                      {errors.intake_id && <p className="text-red-400 text-xs mt-2">{errors.intake_id}</p>}
                    </div>
                  )
                })()}

                {formData.user_type === 'trainer' && (
                  <div>
                    <label className="cinema-label block mb-3 flex items-center gap-2">
                      <BookOpen size={16} className="text-cinema-gold" />
                      Courses You Will Train
                    </label>
                    {loadingCourses ? (
                      <div className="text-cinema-muted text-sm font-sans">Loading courses...</div>
                    ) : courses.length > 0 ? (
                      <div className="space-y-2">
                        {courses.map((course) => {
                          const checked = formData.course_ids.includes(String(course.id))
                          return (
                            <label key={course.id} className={`flex items-start gap-3 p-4 cursor-pointer transition-colors border-b ${checked ? 'border-cinema-gold/30 bg-cinema-gold/5' : 'border-white/5 hover:border-white/10'}`}>
                              <input type="checkbox" className="mt-0.5 accent-cinema-gold" checked={checked} disabled={loading} onChange={() => { const id = String(course.id); setFormData(prev => ({ ...prev, course_ids: checked ? prev.course_ids.filter(x => x !== id) : [...prev.course_ids, id] })) }} />
                              <div>
                                <p className="text-sm text-white font-sans font-medium">{course.course_name || course.title}</p>
                                <p className="text-xs text-cinema-muted font-sans">{course.course_code} {course.duration_weeks ? `· ${course.duration_weeks} weeks` : ''}</p>
                              </div>
                            </label>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="text-cinema-muted text-sm font-sans">No courses available at the moment</div>
                    )}
                    {errors.courses && <p className="text-red-400 text-xs mt-2">{errors.courses}</p>}
                  </div>
                )}

            {formData.user_type === 'student' && (
              <>
                {/* Selected course context */}
                {selectedCourses.length > 0 && (
                  <div className="space-y-4">
                    {selectedCourses.map((course) => {
                      const img = courseImages[course.course_code] || courseImages.default
                      return (
                        <div key={course.id} className="relative overflow-hidden border-b border-white/5">
                          <div className="grid sm:grid-cols-[120px_1fr]">
                            <div className="h-24 sm:h-auto">
                              <img src={img} alt={course.course_name || course.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-4">
                              <p className="text-xs uppercase tracking-wider text-cinema-gold font-semibold">{course.course_code}</p>
                              <p className="font-display text-lg text-white mt-1">{course.course_name || course.title}</p>
                              <div className="mt-2 flex gap-4 text-xs text-cinema-muted font-sans">
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {course.duration_weeks} weeks</span>
                                <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" /> {Number(course.fee).toLocaleString()} UGX</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                <div>
                  <label className="cinema-label block mb-3 flex items-center gap-2">
                    <BookOpen size={16} className="text-cinema-gold" />
                    Available programmes
                  </label>
                  {/* The list belongs to the selected intake — admins decide it
                      in Intake → Offerings. */}
                  {bannerIntake && (
                    <p className="text-xs text-cinema-muted font-sans -mt-1 mb-3">
                      Programmes offered in {bannerIntake.name}
                    </p>
                  )}
                  {offeringsNotice && (
                    <p className="text-xs text-amber-400 font-sans mb-3">{offeringsNotice}</p>
                  )}
                  {!formData.intake_id ? (
                    <div className="text-cinema-muted text-sm font-sans">
                      Choose your intake above to see the programmes it offers.
                    </div>
                  ) : loadingOfferings ? (
                    <div className="text-cinema-muted text-sm font-sans">Loading programmes...</div>
                  ) : offerings.length > 0 ? (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 -mr-2">
                      {offerings.map((course) => {
                        const checked = formData.course_ids.includes(String(course.id))
                        return (
                          <label key={course.id} className={`flex items-start gap-4 p-4 cursor-pointer transition-colors border-b ${checked ? 'border-cinema-gold/30 bg-cinema-gold/5' : 'border-white/5 hover:border-white/10'}`}>
                            <input type="checkbox" className="mt-1 w-5 h-5 accent-cinema-gold flex-shrink-0" checked={checked} disabled={loading} onChange={() => { const id = String(course.id); setFormData(prev => ({ ...prev, course_ids: checked ? prev.course_ids.filter(x => x !== id) : [...prev.course_ids, id] })) }} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm sm:text-sm text-white font-sans font-medium leading-tight pr-2">{course.course_name || course.title}</p>
                              <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs text-cinema-muted font-sans">
                                <span className="flex items-center gap-1">
                                  <span className="inline-block w-2 h-2 bg-cinema-gold/50 rounded-full flex-shrink-0"></span>
                                  {course.course_code}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 flex-shrink-0" />
                                  {course.duration_weeks} weeks
                                </span>
                                <span className="flex items-center gap-1">
                                  <CreditCard className="w-3 h-3 flex-shrink-0" />
                                  {Number(course.fee).toLocaleString()} UGX
                                </span>
                              </div>
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-cinema-muted text-sm font-sans">
                      No programmes are currently offered in this intake. Choose another intake or talk to admissions.
                    </div>
                  )}
                  {errors.courses && <p className="text-red-400 text-xs mt-2">{errors.courses}</p>}
                </div>
              </>
            )}
            </div>
          )}

            {/* Step 3: Additional Details */}
            {step === 3 && (
              <div className="space-y-8">
                <div>
                  <span className="cinema-label">Step 03</span>
                  <h2 className="font-display text-2xl text-white mt-3">A few more details</h2>
                </div>

                <div>
                  <label className="cinema-label block mb-3 flex items-center gap-2">
                    <Monitor size={16} className="text-cinema-gold" />
                    How would you like to study? <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { value: 'PHYSICAL', label: 'Physical / On Campus', sub: 'Attend classes physically at SMA according to your assigned schedule.' },
                      { value: 'ONLINE', label: 'Online', sub: 'Attend classes remotely according to the online class schedule.' },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex-1 flex flex-col gap-1 p-4 rounded-2xl border cursor-pointer transition-all ${
                          formData.study_mode === opt.value
                            ? 'border-cinema-gold bg-cinema-gold/10 text-cinema-gold'
                            : 'border-white/10 text-cinema-muted hover:border-white/20'
                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          name="study_mode"
                          value={opt.value}
                          checked={formData.study_mode === opt.value}
                          onChange={(e) => setFormData({ ...formData, study_mode: e.target.value })}
                          disabled={loading}
                          className="sr-only"
                        />
                        <span className="text-sm font-semibold">{opt.label}</span>
                        <span className="text-xs font-light leading-snug">{opt.sub}</span>
                      </label>
                    ))}
                  </div>
                  {errors.study_mode && <p className="text-red-400 text-xs mt-2">{errors.study_mode}</p>}
                </div>

                <div>
                  <label className="cinema-label block mb-3 flex items-center gap-2">
                    <Clock size={16} className="text-cinema-gold" />
                    Preferred Study Time
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { value: '9am', label: '9:00 AM', sub: 'Morning' },
                      { value: '12pm', label: '12:00 PM', sub: 'Afternoon' },
                      { value: '6pm', label: '6:00 PM', sub: 'Evening' },
                    ].map((slot) => (
                      <label
                        key={slot.value}
                        className={`flex-1 min-w-[100px] flex flex-col items-center justify-center gap-1 px-4 py-3 rounded-full border cursor-pointer transition-all ${
                          formData.preferred_study_time === slot.value
                            ? 'border-cinema-gold bg-cinema-gold/10 text-cinema-gold'
                            : 'border-white/10 text-cinema-muted hover:border-white/20'
                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          name="preferred_study_time"
                          value={slot.value}
                          checked={formData.preferred_study_time === slot.value}
                          onChange={(e) => setFormData({ ...formData, preferred_study_time: e.target.value })}
                          disabled={loading}
                          className="sr-only"
                        />
                        <span className="text-sm font-semibold">{slot.label}</span>
                        <span className="text-xs font-light">{slot.sub}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Phase 4.0: Study Days — 3 days per week */}
                <div>
                  <label className="cinema-label block mb-3 flex items-center gap-2">
                    <Calendar size={16} className="text-cinema-gold" />
                    Study Days (3 days per week)
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { value: 'MON_WED_FRI', label: 'Mon / Wed / Fri', sub: 'Morning track' },
                      { value: 'TUE_THU_SAT', label: 'Tue / Thu / Sat', sub: 'Afternoon track' },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex-1 min-w-[140px] flex flex-col items-center justify-center gap-1 px-4 py-3 rounded-full border cursor-pointer transition-all ${
                          formData.study_days === opt.value
                            ? 'border-cinema-gold bg-cinema-gold/10 text-cinema-gold'
                            : 'border-white/10 text-cinema-muted hover:border-white/20'
                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          name="study_days"
                          value={opt.value}
                          checked={formData.study_days === opt.value}
                          onChange={(e) => setFormData({ ...formData, study_days: e.target.value })}
                          disabled={loading}
                          className="sr-only"
                        />
                        <span className="text-sm font-semibold">{opt.label}</span>
                        <span className="text-xs font-light">{opt.sub}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="cinema-label block mb-3">Address</label>
                  <textarea className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0 resize-none font-sans font-light" placeholder="Your address" rows="2" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} disabled={loading} />
                </div>
              </div>
            )}

            {/* Step 4: Account */}
            {step === 4 && (
              <div className="space-y-8">
                <div>
                  <span className="cinema-label">Step 04</span>
                  <h2 className="font-display text-2xl text-white mt-3">Create your account</h2>
                </div>

                <div className="py-5 border-b border-white/5 space-y-3">
                  <p className="cinema-label">Application Summary</p>
                  <div className="space-y-2 text-sm font-sans">
                    <p className="text-white"><span className="text-cinema-muted">Name:</span> {formData.first_name} {formData.last_name}</p>
                    <p className="text-white"><span className="text-cinema-muted">Email:</span> {formData.email}</p>
                    <p className="text-white"><span className="text-cinema-muted">Phone:</span> {formData.phone}</p>
                    <p className="text-white"><span className="text-cinema-muted">Programs:</span> {selectedCourses.map(c => c.course_name || c.title).join(', ') || 'None selected'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="cinema-label block mb-3">Password</label>
                    <input type="password" className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0 font-sans font-light" placeholder="••••••••" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required disabled={loading} minLength="6" />
                    {errors.password && <p className="text-red-400 text-xs mt-2">{errors.password}</p>}
                  </div>
                  <div>
                    <label className="cinema-label block mb-3">Confirm Password</label>
                    <input type="password" className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0 font-sans font-light" placeholder="••••••••" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} required disabled={loading} minLength="6" />
                    {errors.confirmPassword && <p className="text-red-400 text-xs mt-2">{errors.confirmPassword}</p>}
                  </div>
                </div>

                {/* Payment info */}
                <div className="py-5 border-b border-white/5">
                  <p className="cinema-label mb-4">Payment Options</p>
                  <div className="space-y-3 text-sm font-sans">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-cinema-gold mt-0.5 shrink-0" />
                      <div>
                        <p className="text-white">Full payment upfront</p>
                        <p className="text-cinema-muted text-xs">Secure your seat immediately</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-cinema-gold mt-0.5 shrink-0" />
                      <div>
                        <p className="text-white">Flexible 2–3 installment plan</p>
                        <p className="text-cinema-muted text-xs">Spread across your course duration</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-cinema-gold mt-0.5 shrink-0" />
                      <div>
                        <p className="text-white">50,000 UGX registration fee</p>
                        <p className="text-cinema-muted text-xs">Pay at campus or via Mobile Money before your account is activated</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Urgency */}
                <div className="flex items-center gap-3 p-4 bg-cinema-gold/5 border border-cinema-gold/20 rounded-sm">
                  <Calendar className="w-5 h-5 text-cinema-gold shrink-0" />
                  <div>
                    <p className="text-white text-sm font-sans">2026 Intake — Limited Seats</p>
                    <p className="text-cinema-muted text-xs font-sans">Applications reviewed on a rolling basis. Early applicants have priority.</p>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full cinema-btn-primary flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  <CheckCircle className="w-5 h-5" />
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            )}

            {/* Navigation buttons */}
            {step < 4 && (
              <div className="flex gap-4 pt-4">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="cinema-btn-ghost flex items-center gap-2"
                    disabled={loading}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={nextStep}
                  className="cinema-btn-primary flex items-center gap-2 ml-auto"
                  disabled={loading}
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </form>
        </>
        )}

        {/* Footer links */}
        {!submitted && (
            <div className="mt-10 pt-8 border-t border-white/5 text-center">
              <p className="text-cinema-muted text-sm font-sans">
                Already have an account?{' '}
                <Link to="/login" className="text-cinema-gold hover:text-cinema-glow transition-colors font-medium">
                  Sign in here
                </Link>
              </p>
              <Link to="/" className="inline-flex items-center gap-2 text-cinema-muted hover:text-white mt-4 transition-colors text-sm font-sans">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  )
}
